// ============================================================
// ExamPdfEditorPro.jsx - نسخه نهایی با پشتیبانی از ارتفاع تصاویر
// ============================================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './ExamPdfEditor.css';
import { toNumber, sanitizeHtml } from './ExamContext.jsx';
import { getImageUrl } from '../../services/ImageService';

// ============================================================
// ۱. مدل‌های سربرگ (۴ مدل با کادربندی متفاوت)
// ============================================================
const HEADER_MODELS = [
  {
    id: 'official',
    name: 'رسمی آموزش و پرورش',
    icon: '🏛️',
    description: 'مناسب برای دبیرستان‌ها و مدارس دولتی',
  },
  {
    id: 'minimal',
    name: 'مینیمال مدرن',
    icon: '✨',
    description: 'مناسب برای مدارس غیردولتی و آزمون‌های داخلی',
  },
  {
    id: 'kids',
    name: 'کودکان‌پسند',
    icon: '🌈',
    description: 'مناسب برای مدارس ابتدایی',
  },
  {
    id: 'final',
    name: 'نهایی سازمان سنجش',
    icon: '🎯',
    description: 'مناسب برای آزمون‌های نهایی و سراسری',
  },
];

// ============================================================
// ۲. مدل‌های بدنه (۴ مدل چیدمان)
// ============================================================
const BODY_MODELS = [
  { id: 'grouped', name: 'گروه‌بندی با شماره گروه عددی و سوالات الفبایی', icon: '📋', description: 'هر گروه یک کادر، سوالات با الف‌با' },
  { id: 'grouped_reverse', name: 'گروه‌بندی با شماره گروه الفبایی و سوالات عددی', icon: '📊', description: 'هر گروه یک کادر، سوالات با عدد' },
  { id: 'linear', name: 'خطی بدون گروه‌بندی', icon: '📝', description: 'هر سوال یک ردیف مستقل' },
  { id: 'mixed', name: 'ترکیبی (تشریحی جدا)', icon: '📄', description: 'غیرتشریحی گروه‌بندی، تشریحی خطی' },
];

// ============================================================
// ۳. توابع کمکی
// ============================================================
const toPersianDigits = (num) => {
  if (num === undefined || num === null) return '';
  const id = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return num.toString().replace(/[0-9]/g, (w) => id[+w]);
};

const toPersianLetter = (letter) => {
  const map = {
    'A': 'الف', 'B': 'ب', 'C': 'پ', 'D': 'ت', 'E': 'ث', 'F': 'ج',
    'G': 'چ', 'H': 'ح', 'I': 'خ', 'J': 'د', 'K': 'ذ', 'L': 'ر',
    'M': 'ز', 'N': 'ژ', 'O': 'س', 'P': 'ش', 'Q': 'ص', 'R': 'ض',
    'S': 'ط', 'T': 'ظ', 'U': 'ع', 'V': 'غ', 'W': 'ف', 'X': 'ق',
    'Y': 'ک', 'Z': 'گ'
  };
  return map[letter] || letter;
};

const alphabetLetters = ['الف', 'ب', 'پ', 'ت', 'ث', 'ج', 'چ', 'ح', 'خ', 'د', 'ذ', 'ر', 'ز', 'ژ', 'س', 'ش', 'ص', 'ض', 'ط', 'ظ', 'ع', 'غ', 'ف', 'ق', 'ک', 'گ', 'ل', 'م', 'ن', 'و', 'ه', 'ی'];

// ============================================================
// ۴. کامپوننت اصلی
// ============================================================
const ExamPdfEditorPro = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const examData = location.state?.examData || null;

  const [step, setStep] = useState(1);
  const [selectedHeader, setSelectedHeader] = useState(HEADER_MODELS[0]);
  const [selectedBody, setSelectedBody] = useState(BODY_MODELS[0]);
  const [footerText, setFooterText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);


  const [watermarkText, setWatermarkText] = useState('Yadoo'); // ← اضافه شود

const [isDarkMode, setIsDarkMode] = useState(() => {
  try {
    return localStorage.getItem('navbar2-drawer-dark') === '1';
  } catch {
    return false;
  }
});

// ذخیره در کلید مشترک Navbar2
useEffect(() => {
  try {
    localStorage.setItem('navbar2-drawer-dark', isDarkMode ? '1' : '0');
  } catch {}
}, [isDarkMode]);

// گوش دادن به تغییرات Navbar2 (storage بین تب‌ها + polling داخل همون تب)
useEffect(() => {
  const handleStorage = (e) => {
    if (e.key === 'navbar2-drawer-dark') {
      setIsDarkMode(e.newValue === '1');
    }
  };
  window.addEventListener('storage', handleStorage);

  const interval = setInterval(() => {
    try {
      const current = localStorage.getItem('navbar2-drawer-dark') === '1';
      setIsDarkMode((prev) => (prev !== current ? current : prev));
    } catch {}
  }, 500);

  return () => {
    window.removeEventListener('storage', handleStorage);
    clearInterval(interval);
  };
}, []);

  const [headerFields, setHeaderFields] = useState({
    province: '',
    district: '',
    schoolType: 'دبیرستان',
    schoolName: '',
    date: '',
    time: '',
    period: 'صبح',
    examCode: '',
  });

  const handleFieldChange = (key, value) => {
    setHeaderFields(prev => ({ ...prev, [key]: value }));
  };

  const goToStep2 = () => setStep(2);
  const goToStep1 = () => setStep(1);

  if (!examData) {
    return (
<div className={`pdf-editor-pro-container ${isDarkMode ? 'dark-mode' : ''}`}>
  <div className="error-box">
          <span className="error-icon">⚠️</span>
          <h2>داده‌های آزمون یافت نشد</h2>
          <p>لطفاً ابتدا از صفحه ساخت آزمون اقدام کنید.</p>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>بازگشت</button>
        </div>
      </div>
    );
  }

  const totalScore = examData.questions?.reduce((sum, q) => sum + toNumber(q.score, 1), 0) || 0;

  // ===== 🔽 CHANGED: مقادیر نمایشی با fallback =====
  // - عنوان درس: اگر subject خالی بود، از title یا book استفاده می‌شود
  // - رشته: از fieldTitle استفاده می‌شود
  // - دبیر: اگر teacherName نبود، از کاربر جاری یا لیست users استخراج می‌شود
  const displaySubject =
    examData.subject || examData.title || examData.book || '---';

  const displayMajor =
    examData.fieldTitle || examData.major || '---';

  const displayTeacher = (() => {
    if (examData.teacherName) return examData.teacherName;
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const u = JSON.parse(storedUser);
        if (!examData.teacherId || u.id === examData.teacherId) {
          const fullName =
            (u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : '') ||
            u.name || u.fullName || u.username || '';
          if (String(fullName).trim()) return String(fullName).trim();
        }
      }
      if (examData.teacherId) {
        const storedUsers = localStorage.getItem('users');
        if (storedUsers) {
          const users = JSON.parse(storedUsers);
          const found = users.find((x) => x.id === examData.teacherId);
          if (found) {
            const fullName =
              (found.firstName && found.lastName ? `${found.firstName} ${found.lastName}` : '') ||
              found.name || found.fullName || found.username || '';
            if (String(fullName).trim()) return String(fullName).trim();
          }
        }
      }
    } catch (e) {
      // نادیده بگیر
    }
    return '---';
  })();

  // ============================================================
  // ۵. رندر سربرگ‌ها (۴ مدل) - بدون تغییر
  // ============================================================
  // 🔒 escape مقادیر قبل از تزریق در HTML (جلوگیری از XSS)
  const esc = (s) => String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const renderHeaderHTML = () => {
    const h = {
      province: esc(headerFields.province),
      district: esc(headerFields.district),
      schoolType: esc(headerFields.schoolType),
      schoolName: esc(headerFields.schoolName),
      date: esc(headerFields.date),
      time: esc(headerFields.time),
      period: esc(headerFields.period),
      examCode: esc(headerFields.examCode),
    };
    const info = examData;
    const grade = esc(info.grade || '---');
    const subject = esc(displaySubject);
    const duration = esc(info.duration || '---');
    const teacher = esc(displayTeacher);
    const major = esc(displayMajor);

    const wrapper = (content) => `
      <div style="border: 2px solid #000000; padding: 8px 12px; margin-bottom: 8px; background: #ffffff; direction: rtl; font-family: 'Vazirmatn', 'Tahoma', sans-serif; font-size: 15px; color: #000000;">
        ${content}
      </div>
    `;

    const bismillah = `
      <div style="text-align: center; font-size: 17px; font-weight: 700; color: #000000; margin-bottom: 3px; letter-spacing: 1px;">
        ﷽
      </div>
    `;

    switch (selectedHeader.id) {
      case 'official':
        return wrapper(`
          ${bismillah}
          <div style="border: 1px solid #000000; padding: 6px 10px;">
            <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1px dashed #00000091; padding-bottom: 5px; margin-bottom: 5px;">
              <div style="text-align: right; flex: 1; padding-left: 6px;">
                <div style="font-size: 14px; font-weight: 600;">نام درس: ${subject}</div>
                <div style="font-size: 14px; font-weight: 600;">پایه: ${grade}</div>
                <div style="font-size: 14px; font-weight: 600;">رشته: ${major}</div>
                <div style="font-size: 14px; font-weight: 600;">دبیر: ${teacher}</div>
              </div>
              <div style="text-align: center; flex: 1.5; padding: 0 6px;">
                <div style="font-size: 13px; font-weight: 600;">وزارت آموزش و پرورش</div>
                <div style="font-size: 13px; font-weight: 600;">استان ${h.province || '......'}</div>
                <div style="font-size: 13px; font-weight: 600;">منطقه ${h.district || '......'}</div>
                <div style="font-size: 15px; font-weight: 800;">${h.schoolType || 'دبیرستان'} ${h.schoolName || '..........'}</div>
              </div>
              <div style="text-align: left; flex: 1; padding-right: 6px;">
                <div style="font-size: 14px; font-weight: 600;">تاریخ: ${h.date || '......'}</div>
                <div style="font-size: 14px; font-weight: 600;">ساعت: ${h.time || '......'} ${h.period || ''}</div>
                <div style="font-size: 14px; font-weight: 600;">مدت: ${duration}</div>
              </div>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 13px; padding-top: 4px;">
              <span><strong>نام:</strong> ....................</span>
              <span><strong>نام خانوادگی:</strong> ....................</span>
              <span><strong>نام پدر:</strong> ....................</span>
              <span><strong>صفحات:</strong> ۱</span>
            </div>
          </div>
        `);

      case 'minimal':
        return wrapper(`
          ${bismillah}
          <div style="border: 1px solid #000000; padding: 6px 10px;">
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 3px 0; border-bottom: 1px dashed #00000091; margin-bottom: 5px;">
              <div style="text-align: right; flex: 1;">
                <div style="font-size: 14px; font-weight: 600;">درس: ${subject}</div>
                <div style="font-size: 14px; font-weight: 600;">پایه: ${grade}</div>
                <div style="font-size: 14px; font-weight: 600;">دبیر: ${teacher}</div>
              </div>
              <div style="text-align: center; flex: 1.5;">
                <div style="font-size: 19px; font-weight: 800;">${h.schoolName || 'مدرسه'}</div>
                <div style="font-size: 13px;">${h.schoolType || ''} ${h.district ? '- ' + h.district : ''}</div>
              </div>
              <div style="text-align: left; flex: 1;">
                <div style="font-size: 14px; font-weight: 600;">تاریخ: ${h.date || '......'}</div>
                <div style="font-size: 14px; font-weight: 600;">ساعت: ${h.time || '......'} ${h.period || ''}</div>
                <div style="font-size: 14px; font-weight: 600;">مدت: ${duration}</div>
              </div>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 13px;">
              <span><strong>نام:</strong> ....................</span>
              <span><strong>نام خانوادگی:</strong> ....................</span>
              <span><strong>نام پدر:</strong> ....................</span>
            </div>
          </div>
        `);

      case 'kids':
        return wrapper(`
          <div style="border: 3px double #000000; border-radius: 14px; padding: 8px 12px;">
            <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #000000; padding-bottom: 5px; margin-bottom: 5px;">
              <div style="text-align: right; flex: 1;">
                <div style="font-size: 15px; font-weight: 700;">🧮 امتحان ${subject}</div>
                <div style="font-size: 14px;">پایه ${grade} ${h.schoolType ? '- ' + h.schoolType : ''}</div>
                <div style="font-size: 14px;">آموزگار: ${teacher}</div>
              </div>
              <div style="text-align: center; flex: 1.5;">
                <div style="font-size: 21px; font-weight: 900;">🌸 بسمه تعالی 🌸</div>
                <div style="font-size: 19px; font-weight: 800;">${h.schoolName || 'دبستان'}</div>
                <div style="font-size: 13px;">${h.schoolType || ''} ${h.district ? '- ' + h.district : ''}</div>
              </div>
              <div style="text-align: left; flex: 1;">
                <div style="font-size: 14px; font-weight: 600;">📅 ${h.date || '......'}</div>
                <div style="font-size: 14px; font-weight: 600;">⏱ ${h.time || '......'} ${h.period || ''}</div>
                <div style="font-size: 14px; font-weight: 600;">⏳ ${duration}</div>
              </div>
            </div>
            <div style="display: flex; justify-content: center; gap: 25px; font-size: 14px; padding-top: 4px; border-top: 2px solid #000000;">
              <span><strong>نام:</strong> ....................</span>
              <span><strong>نام خانوادگی:</strong> ....................</span>
              <span><strong>نام پدر:</strong> ....................</span>
            </div>
          </div>
        `);

      case 'final':
        return wrapper(`
          ${bismillah}
          <div style="border: 3px solid #000000; padding: 6px 10px;">
            <div style="text-align: center; border-bottom: 2px solid #000000; padding-bottom: 3px; margin-bottom: 5px;">
              <div style="font-size: 13px; font-weight: 600;">جمهوری اسلامی ایران - وزارت آموزش و پرورش</div>
              <div style="font-size: 17px; font-weight: 900;">آزمون نهایی</div>
              <div style="font-size: 14px; font-weight: 700;">${h.schoolType || 'دبیرستان'} ${h.schoolName || '..........'}</div>
              <div style="font-size: 13px;">استان ${h.province || '......'} - شهرستان ${h.district || '......'}</div>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 0; border: 2px solid #000000; margin: 3px 0;">
              <div style="padding: 3px; text-align: center; border-left: 1px solid #000000; background: #f8f8f8;">
                <div style="font-size: 12px; font-weight: 600;">نام درس</div>
                <div style="font-size: 14px; font-weight: 700;">${subject}</div>
              </div>
              <div style="padding: 3px; text-align: center; border-left: 1px solid #000000; background: #f8f8f8;">
                <div style="font-size: 12px; font-weight: 600;">پایه / رشته</div>
                <div style="font-size: 14px; font-weight: 700;">${grade} - ${major}</div>
              </div>
              <div style="padding: 3px; text-align: center; border-left: 1px solid #000000; background: #f8f8f8;">
                <div style="font-size: 12px; font-weight: 600;">مدت زمان</div>
                <div style="font-size: 14px; font-weight: 700;">${duration}</div>
              </div>
              <div style="padding: 3px; text-align: center; background: #f8f8f8;">
                <div style="font-size: 12px; font-weight: 600;">کد آزمون</div>
                <div style="font-size: 14px; font-weight: 700;">${h.examCode || '---'}</div>
              </div>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 13px; border-top: 2px solid #000000; padding-top: 4px; margin-top: 3px;">
              <span><strong>تاریخ:</strong> ${h.date || '......'}</span>
              <span><strong>ساعت:</strong> ${h.time || '......'} ${h.period || ''}</span>
              <span><strong>دبیر:</strong> ${teacher}</span>
              <span><strong>صفحات:</strong> ۱</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 13px; border-top: 1px solid #000000; padding-top: 3px; margin-top: 3px;">
              <span><strong>نام:</strong> ....................</span>
              <span><strong>نام خانوادگی:</strong> ....................</span>
              <span><strong>نام پدر:</strong> ....................</span>
              <span><strong>شماره صندلی:</strong> ______</span>
            </div>
          </div>
        `);

      default:
        return '';
    }
  };

  // ============================================================
  // ۶. تولید آیتم‌های بدنه با پشتیبانی از ارتفاع تصویر
  // ============================================================
  const buildQuestionHTML = (q, questionNumber) => {
    let html = `
      <tr style="border-bottom: 1px solid #000000d4;">
        <td style="padding: 3px 5px; text-align: center; font-weight: 700; border-left: 1px solid #000000; border-bottom: 1px solid #000000; vertical-align: top; background: #f8f8f8; min-width: 32px; font-size: 14px;">
          ${questionNumber}
        </td>
        <td style="padding: 3px 6px; border-left: 1px solid #000000; vertical-align: top;">
          <div style="font-size: 16px; margin: 7px; line-height: 1.7; color: #000000;">${sanitizeHtml(q.question)}</div>
    `;

    if (q.image) {
      const imageUrl = getImageUrl(q.image);
      html += `
        <div style="text-align: left; padding: 6px 0; background: #fafafa; border-radius: 6px; margin: 4px 0; min-height: 80px;">
          <img 
            src="${imageUrl}" 
            alt="تصویر سوال"
            style="
              max-width: 250px; 
              max-height: 250px; 
              width: auto; 
              height: auto; 
              object-fit: contain; 
              border: 1px solid #ddd; 
              border-radius: 6px; 
              box-shadow: 0 2px 6px rgba(0,0,0,0.1);
              background: #ffffff;
              min-height: 60px;
            " 
          />
        </div>
      `;
    }
    if (q.mathFormula) {
      html += `<div style="font-size:14px; background:#f0f0f0; padding:2px 6px; border-radius:3px; display:inline-block; margin:2px 0; border-right:3px solid #000000;">📐 ${q.mathFormula}</div>`;
    }

    if (q.type === 'multiple' && q.options) {
      html += `<div style="margin-right:14px; margin-top:2px; display:grid; grid-template-columns:1fr 1fr; gap:1px 14px;">`;
      q.options.forEach((opt, oi) => {
        const letter = toPersianLetter(String.fromCharCode(65 + oi));
        html += `<div style="padding:1px 0; font-size:14px; margin-bottom: 7px ; color:#000000;"><span style="font-weight:700;">${letter}.</span> ${opt}</div>`;
      });
      html += `</div>`;
    }

    if (q.type === 'truefalse') {
      html += `<div style="margin-right:14px; margin-top:2px; margin-bottom:2px; display:flex; gap:16px; font-size:14px; color:#000000;"><span>○ صحیح</span> <span>○ غلط</span></div>`;
    }

    if (q.type === 'essay') {
      html += `<div style="border:1px dashed #00000000; height:65px; margin-bottom:3px; border-radius:3px; margin-top:3px; background:#fafafa;"></div>`;
    }

    if (q.type === 'fill' && q.fillAnswers) {
      html += `<div style="margin-right:14px; margin-top:2px;">`;
      q.fillAnswers.forEach((_, fi) => {
        html += `<div style="display:flex; align-items:center; gap:5px; font-size:14px; margin-bottom:3px; padding:1px 0; color:#000000;">
          <span style="font-weight:600;">جای خالی ${toPersianDigits(fi+1)}:</span>
          <span style="border-bottom:2px dashed #000000 ; margin-bottom:3px  ; padding:0 20px;">&nbsp;</span>
        </div>`;
      });
      html += `</div>`;
    }

    if (q.type === 'matching' && q.matchingPairs) {
      html += `<div style="margin-right:14px; margin-top:2px ; margin-bottom:3px ;">`;
      q.matchingPairs.forEach((pair, pi) => {
        const letter = toPersianLetter(String.fromCharCode(65 + pi));
        html += `
          <div style="display:flex; align-items:center; gap:4px; font-size:14px; padding:1px 0; color:#000000;">
            <span>${pair.left}</span> <span style="font-weight:700;">⟷</span>
            <span style="border-bottom:1px dashed #000000; padding:0 10px;">&nbsp;</span>
            <span style="font-size:12px;">(${letter})</span>
          </div>
        `;
      });
      html += `</div>`;
    }

    if (q.type === 'ordering' && q.orderItems) {
      html += `<div style="margin-right:14px; margin-top:2px ; margin-bottom:3px ;">`;
      q.orderItems.forEach((item, oi) => {
        html += `<div style="display:flex; align-items:center; gap:5px; font-size:14px; padding:1px 0; color:#000000;">
          <span style="font-weight:600;">${toPersianDigits(oi+1)}.</span> <span>${item}</span>
          <span style="border-bottom:1px dashed #000000; padding:0 10px;">&nbsp;</span>
        </div>`;
      });
      html += `</div>`;
    }

    html += `
        </td>
        <td style="padding: 3px 5px; text-align: center; font-weight: 700; vertical-align: top; border-bottom: 1px solid #000000; background: #f8f8f8; min-width: 32px; font-size: 14px;">
          ${toPersianDigits(q.score)}
        </td>
      </tr>
    `;
    return html;
  };

  const buildGroupTitleHTML = (label, groupNumber) => {
    return `
      <tr style="border-bottom: 2px solid #000000;">
        <td style="padding: 5px 4px; text-align: center; font-weight: 700; border-left: 2px solid #000000; border-bottom: 2px solid #000000; vertical-align: middle; background: #f8f8f8; font-size: 16px; min-width: 32px;">
          ${groupNumber}
        </td>
        <td style="padding: 3px 6px; border-left: 2px solid #000000; vertical-align: top; background: #ffffff00;">
          <div style="font-weight: 700; font-size: 15px; border-bottom: 1px solid #00000085; padding-bottom: 2px; margin-bottom: 3px; color: #000000;">
            ${label}
          </div>
        </td>
        <td style="padding: 3px 3px; border-bottom: 2px solid #000000; text-align: center; font-weight: 700; vertical-align: middle; background: #f8f8f8; font-size: 13px; min-width: 0px;">
          <!-- نمره گروه -->
        </td>
      </tr>
    `;
  };

  const buildTotalRowHTML = () => {
    return `
      <tr style="background: #ffffff; border-top: 0px solid #ffffff00; font-weight: 700;">
        <td colspan="2" style="padding: 4px 6px; text-align: left; border-left: 1px solid #000000; font-size: 15px;">
          جمع کل نمرات
        </td>
        <td style="padding: 4px 5px; text-align: center; font-size: 16px;">
          ${toPersianDigits(totalScore)}
        </td>
      </tr>
    `;
  };

  const getBodyItems = () => {
    const questions = examData.questions || [];
    if (!questions.length) return [];

    const grouped = {
      multiple: [],
      truefalse: [],
      fill: [],
      essay: [],
      matching: [],
      ordering: [],
    };
    questions.forEach((q, idx) => {
      const type = q.type || 'multiple';
      if (grouped[type]) grouped[type].push({ ...q, index: idx });
    });

    const items = [];
    const bodyId = selectedBody.id;

    // هدر جدول
    const tableHeader = `
      <thead>
        <tr style="background: #e8e8e8; border-bottom: 2px solid #000000;">
          <th style="padding: 2px 0px; text-align: center; border-left: 2px solid #000000; font-weight: 300; width: 1%; font-size: 12px;">ردیف</th>
          <th style="padding: 4px 5px; text-align: center; border-left: 2px solid #000000; font-weight: 300; width: 98%; font-size: 12px;">سوالات</th>
          <th style="padding: 2px 0px; text-align: center; font-weight: 700; width: 1%; font-size: 12px;">نمره</th>
        </tr>
      </thead>
    `;
    items.push({ type: 'table_header', html: tableHeader });

    if (bodyId === 'grouped' || bodyId === 'grouped_reverse') {
      const useAlphabet = bodyId === 'grouped';
      let groupCounter = 1;
      const groups = [
        { type: 'multiple', label: 'سوالات تستی (تک‌انتخابی)' },
        { type: 'truefalse', label: 'سوالات صحیح / غلط' },
        { type: 'fill', label: 'سوالات جای خالی' },
        { type: 'essay', label: 'سوالات تشریحی' },
        { type: 'matching', label: 'سوالات تطبیقی' },
        { type: 'ordering', label: 'سوالات مرتب‌سازی' },
      ];

      groups.forEach(g => {
        const gItems = grouped[g.type] || [];
        if (gItems.length) {
          const groupNumber = useAlphabet ? toPersianDigits(groupCounter) : (alphabetLetters[groupCounter-1] || String.fromCharCode(64+groupCounter));
          items.push({
            type: 'group_title',
            html: buildGroupTitleHTML(`${g.label} (${gItems.length} سوال)`, groupNumber),
          });
          gItems.forEach((q, idx) => {
            let qNumber;
            if (useAlphabet) {
              qNumber = alphabetLetters[idx] || String.fromCharCode(65 + idx);
            } else {
              qNumber = toPersianDigits(idx + 1);
            }
            items.push({
              type: 'question',
              html: buildQuestionHTML(q, qNumber),
            });
          });
          groupCounter++;
        }
      });
    } else if (bodyId === 'linear') {
      questions.forEach((q, idx) => {
        items.push({
          type: 'question',
          html: buildQuestionHTML(q, toPersianDigits(idx + 1)),
        });
      });
    } else if (bodyId === 'mixed') {
      let groupCounter = 1;
      const nonEssayGroups = [
        { type: 'multiple', label: 'سوالات تستی (تک‌انتخابی)' },
        { type: 'truefalse', label: 'سوالات صحیح / غلط' },
        { type: 'fill', label: 'سوالات جای خالی' },
        { type: 'matching', label: 'سوالات تطبیقی' },
        { type: 'ordering', label: 'سوالات مرتب‌سازی' },
      ];
      nonEssayGroups.forEach(g => {
        const gItems = grouped[g.type] || [];
        if (gItems.length) {
          const groupNumber = toPersianDigits(groupCounter);
          items.push({
            type: 'group_title',
            html: buildGroupTitleHTML(`${g.label} (${gItems.length} سوال)`, groupNumber),
          });
          gItems.forEach((q, idx) => {
            items.push({
              type: 'question',
              html: buildQuestionHTML(q, alphabetLetters[idx] || String.fromCharCode(65 + idx)),
            });
          });
          groupCounter++;
        }
      });
      const essayItems = grouped.essay || [];
      if (essayItems.length) {
        essayItems.forEach((q, idx) => {
          items.push({
            type: 'question',
            html: buildQuestionHTML(q, toPersianDigits(groupCounter + idx)),
          });
        });
      }
    }

    items.push({
      type: 'total',
      html: buildTotalRowHTML(),
    });

    return items;
  };

  // ============================================================
  // ۷. تولید PDF با اندازه‌گیری واقعی و پشتیبانی از تصاویر
  // ============================================================
  const generatePDF = async () => {
    setIsGenerating(true);

    try {
      // ---- تنظیمات ثابت ----
      const PDF_MARGIN_MM = 9;
      const PDF_WIDTH_MM = 210;
      const PDF_HEIGHT_MM = 297;
      const PRINTABLE_WIDTH_MM = PDF_WIDTH_MM - 2 * PDF_MARGIN_MM;
      const RENDER_WIDTH_PX = 780;
      const PX_TO_MM = PRINTABLE_WIDTH_MM / RENDER_WIDTH_PX;
      const PAGE_HEIGHT_PX = (PDF_HEIGHT_MM - 2 * PDF_MARGIN_MM) / PX_TO_MM;
      const BOTTOM_PADDING_PX = 0;
      const SEPARATOR_HEIGHT_PX = 22;
      const CONTENT_HEIGHT_PX = PAGE_HEIGHT_PX - BOTTOM_PADDING_PX - 3;

      // ---- تابع اندازه‌گیری ارتفاع با DOM (برای تخمین اولیه) ----
      const measureHeight = (html) => {
        const div = document.createElement('div');
        div.style.position = 'fixed';
        div.style.left = '-9999px';
        div.style.top = '0';
        div.style.width = RENDER_WIDTH_PX + 'px';
        div.style.background = '#ffffff';
        div.style.padding = '0';
        div.style.zIndex = '-1';
        div.innerHTML = html;
        document.body.appendChild(div);
        const height = div.scrollHeight;
        document.body.removeChild(div);
        return height;
      };

      // ---- تابع پیش‌بارگذاری تصاویر در HTML ----
      const preloadImagesInHTML = async (html) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const imgs = tempDiv.querySelectorAll('img');
        const promises = Array.from(imgs).map(img => {
          return new Promise((resolve) => {
            if (img.complete) {
              resolve();
            } else {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            }
          });
        });
        await Promise.all(promises);
      };

      // ---- تابع اندازه‌گیری ارتفاع واقعی با html2canvas (با پیش‌بارگذاری تصاویر) ----
      const measureRealHeight = async (html) => {
        // پیش‌بارگذاری تصاویر برای اطمینان از رندر صحیح
        await preloadImagesInHTML(html);

        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.left = '-9999px';
        container.style.top = '0';
        container.style.width = RENDER_WIDTH_PX + 'px';
        container.style.background = '#ffffff';
        container.style.padding = '0';
        container.style.zIndex = '-1';
        container.innerHTML = html;
        document.body.appendChild(container);

        try {
          const canvas = await html2canvas(container, {
            scale: 1,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: '#ffffff',
            width: RENDER_WIDTH_PX,
          });
          const realHeight = canvas.height;
          document.body.removeChild(container);
          return realHeight;
        } catch (error) {
          document.body.removeChild(container);
          throw error;
        }
      };

      // ---- تولید آیتم‌ها ----
      const bodyItems = getBodyItems();
      if (!bodyItems.length) {
        alert('هیچ سوالی برای نمایش وجود ندارد.');
        setIsGenerating(false);
        return;
      }

      // ---- اندازه‌گیری تخمینی ارتفاع آیتم‌ها ----
      const itemsWithHeight = bodyItems.map(item => {
        let htmlToMeasure = item.html;
        if (item.type === 'table_header') {
          htmlToMeasure = `<table style="width:100%; border-collapse:collapse; border:2px solid #000000; font-size:12px; color:#000000; direction:rtl;">${item.html}</table>`;
        } else if (item.type === 'group_title' || item.type === 'question' || item.type === 'total') {
          htmlToMeasure = `<table style="width:100%; border-collapse:collapse; border:2px solid #000000; font-size:12px; color:#000000; direction:rtl;"><tbody>${item.html}</tbody></table>`;
        }
        const measured = measureHeight(htmlToMeasure);
        // ضریب ۱.۰۴ برای جبران خطای تصاویر و سایر المان‌ها
        const height = measured * 1.04;
        return { ...item, height };
      });

      // ---- محاسبه ارتفاع سربرگ‌ها ----
      const fullHeaderHTML = renderHeaderHTML();
      const fullHeaderHeight = measureHeight(`<div style="width:${RENDER_WIDTH_PX}px;">${fullHeaderHTML}</div>`) * 1.04;

      const compactHeaderHTML = () => {
        const h = headerFields;
        const info = examData;
        return `
          <div style="border: 1px solid #000000; padding: 2px 6px; margin-bottom: 4px; background: #f8f8f8; direction: rtl; font-family: 'Vazirmatn', 'Tahoma', sans-serif; font-size: 14px; color: #000000; display: flex; justify-content: space-between; align-items: center;">
            <span><strong>${h.schoolName || 'مدرسه'}</strong> - ${displaySubject} (پایه ${info.grade || ''})</span>
            <span>تاریخ: ${h.date || '......'}</span>
            <span>صفحه: <span class="pageNum"></span></span>
          </div>
        `;
      };
      const compactHeaderHeight = measureHeight(`<div style="width:${RENDER_WIDTH_PX}px;">${compactHeaderHTML()}</div>`) * 1.04;

      // ---- تابع ساخت HTML کامل یک صفحه ----
      const buildPageHTML = (pageItems, isFirst, isLast) => {
  let headerHtml = '';
  let tableRows = [];

  pageItems.forEach(item => {
    if (item.type === 'page_header') {
      headerHtml = item.html;
    } else if (item.type === 'table_header') {
      tableRows.push({ type: 'thead', html: item.html });
    } else if (item.type === 'group_title' || item.type === 'question' || item.type === 'total') {
      tableRows.push({ type: 'tbody', html: item.html });
    }
  });

  let tableHtml = '';
  if (tableRows.length) {
    let theadHtml = '';
    let tbodyHtml = '';
    tableRows.forEach(row => {
      if (row.type === 'thead') {
        theadHtml = row.html;
      } else {
        tbodyHtml += row.html;
      }
    });
    tableHtml = `
      <table style="width: 100%; border-collapse: collapse; border: 2px solid #000000; font-size: 14px; color: #000000; direction: rtl; margin-top: 4px; position: relative; z-index: 1;">
        ${theadHtml}
        ${tbodyHtml ? `<tbody>${tbodyHtml}</tbody>` : ''}
      </table>
    `;
  }

  const separatorLine = !isLast ? `
    <div style="flex-shrink: 0; height: ${SEPARATOR_HEIGHT_PX}px; display: flex; align-items: flex-end; border-top: 1px solid #000000c3; margin: 0 4px; padding-top: 0px; position: relative; z-index: 1;">
      <span style="font-size: 0px; color: #555; margin-right: auto;">➡️ ادامه در صفحه بعد</span>
    </div>
  ` : '';

  // ===== 🔽 واترمارک =====
  const safeWatermark = String(watermarkText || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const watermarkHTML = safeWatermark.trim() ? `
    <div style="
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-32deg);
      font-family: 'Vazirmatn', 'Tahoma', sans-serif;
      font-size: 150px;
      font-weight: 900;
      color: rgba(0, 0, 0, 0.055);
      white-space: nowrap;
      pointer-events: none;
      user-select: none;
      letter-spacing: 8px;
      line-height: 1;
      z-index: 0;
      text-shadow: 0 0 1px rgba(0,0,0,0.02);
    ">${safeWatermark}</div>
  ` : '';
  // =======================

  return `
    <div style="
      position: relative;
      display: flex; 
      flex-direction: column; 
      width: ${RENDER_WIDTH_PX}px; 
      height: ${PAGE_HEIGHT_PX}px; 
      background: #ffffff; 
      padding: 0; 
      margin: 0; 
      direction: rtl; 
      font-family: 'Vazirmatn', 'Tahoma', sans-serif; 
      font-size: 14px; 
      color: #000000; 
      line-height: 1.7; 
      overflow: hidden;
    ">
      ${watermarkHTML}
      ${headerHtml ? `<div style="flex-shrink: 0; position: relative; z-index: 1;">${headerHtml}</div>` : ''}
      ${tableHtml ? `<div style="flex: 1; overflow: hidden; min-height: 10px; position: relative; z-index: 1;">${tableHtml}</div>` : ''}
      <div style="flex-shrink: 0; height: ${BOTTOM_PADDING_PX}px; min-height: ${BOTTOM_PADDING_PX}px;"></div>
      ${separatorLine}
    </div>
  `;
};

      // ============================================================
      // مرحله ۱: چیدمان اولیه با تخمین (ضریب بیشتر برای جبران تصاویر)
      // ============================================================
      const initialPages = [];
      let currentPageItems = [];
      let currentPageHeight = 0;
      let isFirstPage = true;
      let hasTableHeader = false;

      const startNewPage = () => {
        if (currentPageItems.length > 0) {
          initialPages.push({ items: [...currentPageItems], isFirst: isFirstPage });
          currentPageItems = [];
          currentPageHeight = 0;
          isFirstPage = false;
          hasTableHeader = false;
        }
      };

      const initPage = () => {
        if (currentPageItems.length === 0) {
          const headerHtml = isFirstPage ? fullHeaderHTML : compactHeaderHTML();
          const headerH = isFirstPage ? fullHeaderHeight : compactHeaderHeight;
          currentPageItems.push({ type: 'page_header', html: headerHtml, height: headerH });
          currentPageHeight += headerH;

          if (!hasTableHeader) {
            const headerItem = itemsWithHeight.find(item => item.type === 'table_header');
            if (headerItem) {
              currentPageItems.push({ ...headerItem });
              currentPageHeight += headerItem.height;
              hasTableHeader = true;
            }
          }
        }
      };

      const getRemainingSpace = () => CONTENT_HEIGHT_PX - currentPageHeight;
      const canFit = (height) => getRemainingSpace() >= height;

      const findNextQuestion = (startIndex) => {
        for (let i = startIndex + 1; i < itemsWithHeight.length; i++) {
          if (itemsWithHeight[i].type === 'question') {
            return i;
          }
        }
        return -1;
      };

      let i = 0;
      while (i < itemsWithHeight.length) {
        const item = itemsWithHeight[i];

        if (currentPageItems.length === 0) {
          initPage();
        }

        if (item.type === 'table_header') {
          i++;
          continue;
        }

        if (item.type === 'group_title') {
          const nextQIndex = findNextQuestion(i);
          if (nextQIndex !== -1) {
            const combinedHeight = item.height + itemsWithHeight[nextQIndex].height;
            if (canFit(combinedHeight)) {
              currentPageItems.push(item);
              currentPageHeight += item.height;
              i++;
              continue;
            } else {
              startNewPage();
              continue;
            }
          } else {
            if (canFit(item.height)) {
              currentPageItems.push(item);
              currentPageHeight += item.height;
              i++;
            } else {
              startNewPage();
            }
            continue;
          }
        }

        if (canFit(item.height)) {
          currentPageItems.push(item);
          currentPageHeight += item.height;
          i++;
        } else {
          startNewPage();
          continue;
        }
      }

      if (currentPageItems.length > 0) {
        initialPages.push({ items: [...currentPageItems], isFirst: isFirstPage });
      }

      // ============================================================
      // مرحله ۲: اصلاح با اندازه‌گیری واقعی (با پیش‌بارگذاری تصاویر)
      // ============================================================
      let pages = initialPages;
      let isFullyCorrected = false;
      let maxIterations = 20;
      let iteration = 0;

      while (!isFullyCorrected && iteration < maxIterations) {
        iteration++;
        isFullyCorrected = true;
        const newPages = [];

        for (let p = 0; p < pages.length; p++) {
          const pageInfo = pages[p];
          const pageItems = pageInfo.items;
          const isFirst = pageInfo.isFirst;
          const isLast = p === pages.length - 1;

          const pageHTML = buildPageHTML(pageItems, isFirst, isLast);
          const realHeight = await measureRealHeight(pageHTML);

          // اگر ارتفاع واقعی بیشتر از حد مجاز باشد → بریدگی وجود دارد
          if (realHeight > PAGE_HEIGHT_PX + 1) {
            isFullyCorrected = false;

            // پیدا کردن آخرین آیتم محتوایی (غیر از هدر و هدر جدول)
            let lastContentIndex = -1;
            for (let j = pageItems.length - 1; j >= 0; j--) {
              const item = pageItems[j];
              if (item.type !== 'page_header' && item.type !== 'table_header') {
                lastContentIndex = j;
                break;
              }
            }

            if (lastContentIndex > 0) {
              const itemsToKeep = pageItems.slice(0, lastContentIndex);
              const itemToMove = pageItems[lastContentIndex];

              const newPageItems = [...itemsToKeep];
              const nextPageItems = [itemToMove];

              // هدر جدول را به صفحه بعد اضافه کن
              if (!nextPageItems.some(it => it.type === 'table_header')) {
                const headerItem = itemsWithHeight.find(it => it.type === 'table_header');
                if (headerItem) {
                  nextPageItems.unshift({ ...headerItem });
                }
              }

              // صفحات باقی‌مانده را به nextPageItems اضافه کن
              const remainingItems = pages.slice(p + 1);
              if (remainingItems.length > 0) {
                const nextExistingItems = remainingItems[0].items;
                const filteredNextItems = nextExistingItems.filter(
                  it => it.type !== 'page_header' && it.type !== 'table_header'
                );
                nextPageItems.push(...filteredNextItems);
                for (let r = 1; r < remainingItems.length; r++) {
                  newPages.push({ items: remainingItems[r].items, isFirst: false });
                }
              }

              newPages.push({ items: newPageItems, isFirst: isFirst });
              newPages.push({ items: nextPageItems, isFirst: false });

              break;
            } else {
              break;
            }
          } else {
            newPages.push({ items: pageItems, isFirst: isFirst });
          }
        }

        if (!isFullyCorrected) {
          pages = newPages;
        } else {
          break;
        }
      }

      // ============================================================
      // مرحله ۳: ساخت PDF نهایی
      // ============================================================
      const pdf = new jsPDF({
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      });

      for (let p = 0; p < pages.length; p++) {
        const pageInfo = pages[p];
        const pageItems = pageInfo.items;
        const isFirst = pageInfo.isFirst;
        const isLast = p === pages.length - 1;

        const pageHTML = buildPageHTML(pageItems, isFirst, isLast);

        // پیش‌بارگذاری تصاویر برای رندر نهایی
        await preloadImagesInHTML(pageHTML);

        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.left = '-9999px';
        container.style.top = '0';
        container.style.width = RENDER_WIDTH_PX + 'px';
        container.style.background = '#ffffff';
        container.style.padding = '0';
        container.style.zIndex = '-1';
        container.innerHTML = pageHTML;
        document.body.appendChild(container);

        const canvas = await html2canvas(container, {
          scale: 3.5,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: RENDER_WIDTH_PX,
          height: PAGE_HEIGHT_PX,
        });

        document.body.removeChild(container);

        const imgData = canvas.toDataURL('image/jpeg', 0.98);
        const imgWidth = PRINTABLE_WIDTH_MM;
        const imgHeight = (canvas.height / canvas.width) * imgWidth;

        if (p > 0) pdf.addPage();

        const xOffset = PDF_MARGIN_MM;
        const yOffset = (PDF_HEIGHT_MM - imgHeight) / 2;

        pdf.addImage(imgData, 'JPEG', xOffset, yOffset, imgWidth, imgHeight);
      }

      pdf.save(`${examData.title || 'آزمون'}.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('❌ خطا در تولید PDF. لطفاً دوباره تلاش کنید.');
    } finally {
      setIsGenerating(false);
    }
  };

  // ============================================================
  // ۸. رندر مراحل
  // ============================================================
  const renderStep1 = () => (
    <div className="step-container">
      <h2>🏛️ انتخاب مدل سربرگ</h2>
      <p className="step-desc">یکی از ۴ مدل سربرگ را انتخاب کنید و اطلاعات مورد نیاز را تکمیل کنید.</p>
      <div className="model-grid">
        {HEADER_MODELS.map(model => (
          <div
            key={model.id}
            className={`model-card ${selectedHeader.id === model.id ? 'selected' : ''}`}
            onClick={() => setSelectedHeader(model)}
          >
            <div className="model-icon">{model.icon}</div>
            <div className="model-name">{model.name}</div>
            <div className="model-desc">{model.description}</div>
          </div>
        ))}
      </div>
      <div className="fields-panel">
        <h3>📝 اطلاعات سربرگ</h3>
        <div className="fields-grid">
          <div className="field-group">
            <label>استان</label>
            <input type="text" value={headerFields.province} onChange={e => handleFieldChange('province', e.target.value)} placeholder="مثال: تهران" />
          </div>
          <div className="field-group">
            <label>شهرستان / منطقه</label>
            <input type="text" value={headerFields.district} onChange={e => handleFieldChange('district', e.target.value)} placeholder="مثال: منطقه ۳" />
          </div>
          <div className="field-group">
            <label>نوع مدرسه</label>
            <select value={headerFields.schoolType} onChange={e => handleFieldChange('schoolType', e.target.value)}>
              <option value="دبستان">دبستان</option>
              <option value="دبیرستان">دبیرستان</option>
              <option value="هنرستان">هنرستان</option>
              <option value="مدرسه">مدرسه</option>
            </select>
          </div>
          <div className="field-group">
            <label>نام مدرسه</label>
            <input type="text" value={headerFields.schoolName} onChange={e => handleFieldChange('schoolName', e.target.value)} placeholder="مثال: دبیرستان شهید بهشتی" />
          </div>
          <div className="field-group">
            <label>تاریخ</label>
            <input type="text" value={headerFields.date} onChange={e => handleFieldChange('date', e.target.value)} placeholder="مثال: ۱۴۰۲/۱۲/۱۵" />
          </div>
          <div className="field-group">
            <label>ساعت شروع</label>
            <input type="text" value={headerFields.time} onChange={e => handleFieldChange('time', e.target.value)} placeholder="مثال: ۸:۰۰" />
          </div>
          <div className="field-group">
            <label>نوبت</label>
            <select value={headerFields.period} onChange={e => handleFieldChange('period', e.target.value)}>
              <option value="صبح">صبح</option>
              <option value="عصر">عصر</option>
            </select>
          </div>
          <div className="field-group">
            <label>کد آزمون</label>
            <input type="text" value={headerFields.examCode} onChange={e => handleFieldChange('examCode', e.target.value)} placeholder="مثال: ۴۰۲-۳-۱۵" />
          </div>
        </div>
      </div>
      <div className="auto-fields">
        <p><strong>📌 اطلاعات خودکار از آزمون:</strong></p>
        <ul>
          <li>عنوان درس: <strong>{displaySubject}</strong></li>
          <li>پایه تحصیلی: <strong>{examData.grade || '---'}</strong></li>
          <li>رشته: <strong>{displayMajor}</strong></li>
          <li>مدت زمان: <strong>{examData.duration || '---'} دقیقه</strong></li>
          <li>دبیر: <strong>{displayTeacher}</strong></li>
          <li>تعداد سوالات: <strong>{examData.questions?.length || 0}</strong></li>
          <li>مجموع نمرات: <strong>{toPersianDigits(totalScore)}</strong></li>
        </ul>
      </div>
      <div className="step-actions">
        <button className="btn btn-primary" onClick={goToStep2}>مرحله بعد ←</button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="step-container">
      <h2>📝 انتخاب مدل بدنه</h2>
      <p className="step-desc">۴ مدل چیدمان سوالات را انتخاب کنید.</p>
      <div className="model-grid">
        {BODY_MODELS.map(model => (
          <div
            key={model.id}
            className={`model-card ${selectedBody.id === model.id ? 'selected' : ''}`}
            onClick={() => setSelectedBody(model)}
          >
            <div className="model-icon">{model.icon}</div>
            <div className="model-name">{model.name}</div>
            <div className="model-desc">{model.description}</div>
          </div>
        ))}
      </div>
      <div className="fields-panel">
        <h3>⚙️ تنظیمات نهایی</h3>
        <div className="fields-grid" style={{ gridTemplateColumns: '1fr' }}>
          <div className="field-group">
            <label>متن انتهایی (اختیاری)</label>
            <textarea value={footerText} onChange={e => setFooterText(e.target.value)} rows="3" placeholder="متن دلخواه..." />
          </div>
          {/*<div className="field-group">
  <label>متن واترمارک (پس‌زمینه)</label>
  <input
    type="text"
    value={watermarkText}
    onChange={e => setWatermarkText(e.target.value)}
    placeholder="مثال: بسمه تعالی / آزمون نهایی / نام مدرسه"
  />
</div>*/}
        </div>
      </div>
      <div className="step-actions">
        <button className="btn btn-outline" onClick={goToStep1}>← قبلی</button>
        <button className="btn btn-success" onClick={generatePDF} disabled={isGenerating}>
          {isGenerating ? '⏳ در حال تولید...' : '📄 دانلود PDF'}
        </button>
      </div>
    </div>
  );

return (
  <div className={`pdf-editor-pro-container ${isDarkMode ? 'dark-mode' : ''}`}>
    <header className="editor-header">
        <button className="back-btn" onClick={() => navigate(-1)}>✕</button>
        <h1>🎯 PDF ساز حرفه‌ای</h1>
        <span className="step-indicator">مرحله {step} از ۲</span>
      </header>
      <div className="content">
        {step === 1 ? renderStep1() : renderStep2()}
      </div>
    </div>
  );
};

export default ExamPdfEditorPro;